import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  MapPin,
  Clock,
  Mountain,
  TrendingUp,
  ArrowRight,
  ArrowLeft,
  Shield,
  ChevronDown,
  Navigation,
  Sparkles,
  Backpack,
  Camera,
  Instagram,
  ExternalLink,
  CheckCircle2,
  Phone,
  MessageCircle,
  Mail,
  Dumbbell,
  ListChecks,
  Utensils,
  Heart,
  Target,
  X,
} from "lucide-react";
import ClientLayout from "../../components/Layout/ClientLayout";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import {
  useGetAdventuresDetailQuery,
  useGetAdventuresQuery,
  useGetDestinationsImagesQuery,
} from "../../apiCaller/apis/getApi";
import HtmlContent from "../../utils/DisplayHTML";
import NoImage from "../../assets/images/No_Image_Available.jpg";
import PriceGridCards from "../Programmes/PriceComparison";
import Event1 from "../../assets/images/banners/event1.jpg";
import Event2 from "../../assets/images/banners/event2.jpg";
import Event3 from "../../assets/images/banners/event3.jpg";
import Prepare from "../../assets/images/banners/prepare.jpg";

const EventDetailRedesign = () => {
  const [coverImage, setCoverImage] = useState();
  const [expandedSections, setExpandedSections] = useState({
    stats: false,
    itinerary: true,
    charges: true,
    gear: true,
    gallery: true,
  });
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPricingBanner, setShowPricingBanner] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { eventId } = useParams();
  const navigate = useNavigate();

  const {
    data: adventure,
    isLoading: adventureIsLoading,
  } = GetTableListAPI({ adventureId: eventId }, useGetAdventuresDetailQuery);

  const {
    data: destinationImages,
  } = GetTableListAPI(
    { destinationId: adventure?.destination_data?.slug ?? adventure?.destination_data?.id },
    useGetDestinationsImagesQuery
  );

  const {
    data: otherAdventures,
  } = GetTableListAPI({}, useGetAdventuresQuery);

  useEffect(() => {
    if (destinationImages?.results?.length > 0) {
      setCoverImage(
        destinationImages.results.find((x) => x.is_display_image === true) ||
          destinationImages.results[0]
      );
    }
  }, [destinationImages]);

  useEffect(() => {
    const handleScroll = () => {
      const gallerySection = document.getElementById('gallery-section');
      if (gallerySection) {
        const rect = gallerySection.getBoundingClientRect();
        // Show banner when scrolled past the gallery
        setShowPricingBanner(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner friendly":
        return "from-green-500 to-emerald-500";
      case "moderate":
        return "from-amber-500 to-orange-500";
      case "challenging":
        return "from-orange-500 to-red-500";
      case "extreme":
        return "from-red-600 to-rose-700";
      default:
        return "from-slate-500 to-slate-600";
    }
  };

  if (adventureIsLoading) {
    return (
      <ClientLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex p-6 bg-emerald-100 rounded-full mb-4">
              <Mountain className="w-16 h-16 text-emerald-600 animate-pulse" />
            </div>
            <p className="text-slate-600 text-lg">Loading adventure details...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      {/* Hero Section */}
      <div className="relative h-[500px] lg:h-[600px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${coverImage?.image_url || adventure?.destination_data?.images?.[0]?.image_url || NoImage})`,
          }}
        />

        {/* Mamba Hikers Navy Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/85 via-slate-800/70 to-mamba-sky-blue/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/90 via-transparent to-transparent" />

        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              onClick={() => navigate(-1)}
              className="absolute top-8 left-8 inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-semibold hover:bg-white/20 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </motion.button>

            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full">
              <Sparkles className="w-4 h-4 text-mamba-golden" />
              <span className="text-sm text-white font-montserrat font-semibold tracking-wide">
                ADVENTURE AWAITS
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black font-montserrat text-white mb-6 tracking-tight leading-tight max-w-5xl">
              {adventure?.destination_data?.name}
            </h1>

            <div className="flex flex-wrap gap-3 justify-center mb-8">
              <div className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-medium">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(adventure?.start_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
              <div className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-medium">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {adventure?.destination_data?.location}
                </div>
              </div>
              <div
                className={`px-4 py-2 bg-gradient-to-r ${getDifficultyColor(
                  adventure?.destination_data?.difficulty,
                )} rounded-full text-white text-sm font-bold shadow-lg`}
              >
                {adventure?.destination_data?.difficulty}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="text-white/60"
          >
            <ChevronDown className="w-8 h-8" />
          </motion.div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="bg-white">
        <div className="px-5 md:px-10 lg:px-16 xl:px-32 -mt-32 relative z-20">
          {/* Quick Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            {[
              {
                icon: Calendar,
                label: "Date",
                value: new Date(adventure?.start_date).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  },
                ),
                gradient: "from-white to-mamba-bg-light",
                iconColor: "text-mamba-sky-blue",
                bgIcon:
                  "bg-gradient-to-br from-mamba-sky-blue/10 to-mamba-sky-blue/20",
              },
              {
                icon: MapPin,
                label: "Location",
                value: adventure?.destination_data?.location,
                gradient: "from-white to-mamba-bg-light",
                iconColor: "text-mamba-golden",
                bgIcon:
                  "bg-gradient-to-br from-mamba-golden/10 to-mamba-golden/20",
              },
              {
                icon: Navigation,
                label: "Distance",
                value: `${adventure?.destination_data?.distance_in_km} KM`,
                gradient: "from-white to-mamba-bg-light",
                iconColor: "text-mamba-navy",
                bgIcon: "bg-gradient-to-br from-mamba-navy/10 to-mamba-navy/20",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-6 border-2 border-${item.iconColor.replace("text-", "")}/20 hover:border-${item.iconColor.replace("text-", "")} shadow-md hover:shadow-xl transition-all`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 ${item.bgIcon} rounded-xl shadow-sm`}>
                    <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                  </div>
                  <div className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-navy/70">
                    {item.label}
                  </div>
                </div>
                <div className="text-2xl font-bold font-inter text-mamba-navy">
                  {item.value}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* 2-Column Grid: Description/Gallery (Left) and Pricing (Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-32">
            {/* Left Column: Description and Gallery */}
            <div className="lg:col-span-2 space-y-16">
              {/* Description Section - Gradient Background */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="py-12 px-8 bg-gradient-to-br from-mamba-bg-light to-white rounded-3xl scroll-mt-16 border-2 border-mamba-sky-blue/20"
              >
                <div className="mb-16 text-center">
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                    <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-sky-blue">
                      About This Adventure
                    </span>
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-mamba-navy leading-tight tracking-tight">
                    Your Journey
                    <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                      Begins Here
                    </span>
                  </h2>
                </div>
                <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed">
                  <HtmlContent
                    htmlData={adventure?.destination_data?.description}
                  />
                </div>
              </motion.div>

              {/* Gallery Section - Creative Masonry Layout */}
              {destinationImages?.results?.length > 3 && (
                <motion.div
                  id="gallery-section"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="scroll-mt-16"
                >
                  <div className="mb-16 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent" />
                      <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-navy">
                        Visual Journey
                      </span>
                      <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent" />
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-mamba-navy leading-tight tracking-tight">
                      Moments That Take
                      <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                        Your Breath Away
                      </span>
                    </h2>
                  </div>

                  {/* Creative Masonry Grid */}
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-4 auto-rows-[200px]">
                    {/* Large featured image - spans 2x2 */}
                    {destinationImages.results[0] && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0 }}
                        className="col-span-4 row-span-2 relative rounded-3xl overflow-hidden cursor-pointer group"
                        onClick={() =>
                          setCoverImage(destinationImages.results[0])
                        }
                      >
                        <img
                          src={
                            destinationImages.results[0].image_url || NoImage
                          }
                          alt="Featured"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full mb-3">
                            <Camera className="w-4 h-4 text-white" />
                            <span className="text-xs font-semibold text-white">
                              Featured
                            </span>
                          </div>
                          <h3 className="text-2xl font-black text-white">
                            Breathtaking Views
                          </h3>
                        </div>
                        <div className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowRight className="w-5 h-5 text-white" />
                        </div>
                      </motion.div>
                    )}

                    {/* Tall image - spans 1x2 */}
                    {destinationImages.results[1] && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="col-span-2 row-span-2 relative rounded-3xl overflow-hidden cursor-pointer group"
                        onClick={() =>
                          setCoverImage(destinationImages.results[1])
                        }
                      >
                        <img
                          src={
                            destinationImages.results[1].image_url || NoImage
                          }
                          alt="Gallery 2"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                            <Camera className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Wide image - spans 2x1 */}
                    {destinationImages.results[2] && (
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.15 }}
                        className="col-span-2 row-span-1 relative rounded-3xl overflow-hidden cursor-pointer group"
                        onClick={() =>
                          setCoverImage(destinationImages.results[2])
                        }
                      >
                        <img
                          src={
                            destinationImages.results[2].image_url || NoImage
                          }
                          alt="Gallery 3"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </motion.div>
                    )}

                    {/* Square images */}
                    {destinationImages.results
                      .slice(3, 5)
                      .map((image, index) => (
                        <motion.div
                          key={index + 3}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.2 + index * 0.05 }}
                          className="col-span-2 row-span-1 relative rounded-3xl overflow-hidden cursor-pointer group"
                          onClick={() => setCoverImage(image)}
                        >
                          <img
                            src={image.image_url || NoImage}
                            alt={`Gallery ${index + 4}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <div className="absolute bottom-3 left-3">
                            <div className="w-8 h-8 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="text-xs font-bold text-white">
                                {index + 4}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))}

                    {/* More images indicator if there are extra images */}
                    {destinationImages.results.length > 6 &&
                      destinationImages.results[5] && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3 }}
                          className="col-span-4 row-span-1 relative rounded-3xl overflow-hidden cursor-pointer group"
                          onClick={() => {
                            setSelectedImageIndex(5);
                            setShowGalleryModal(true);
                          }}
                        >
                          <img
                            src={
                              destinationImages.results[5].image_url || NoImage
                            }
                            alt="Gallery 6"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-slate-900/70 group-hover:bg-slate-900/50 transition-colors duration-500" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-5xl font-black text-white mb-2">
                                +{destinationImages.results.length - 5}
                              </div>
                              <p className="text-sm font-semibold text-white/80 uppercase tracking-wider">
                                More Photos
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Column: Pricing Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="sticky top-8 space-y-6"
              >
                {/* Combined Booking & Pricing Card */}
                <div className="bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl border-2 border-mamba-sky-blue/30 overflow-hidden shadow-xl">
                  {/* Header Section - Navy/Sky Blue Gradient */}
                  <div className="bg-gradient-to-r from-mamba-navy to-mamba-sky-blue p-6 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-mamba-golden/30 backdrop-blur-md rounded-full flex items-center justify-center">
                        <Mountain className="w-5 h-5 text-mamba-golden" />
                      </div>
                      <div>
                        <h3 className="text-xl font-black font-montserrat text-white">
                          Ready for Adventure?
                        </h3>
                      </div>
                    </div>
                    <p className="text-mamba-golden text-sm font-dancing italic">
                      when adventure calls...
                    </p>
                  </div>

                  {/* Trip Details */}
                  <div className="p-6 bg-white">
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-3 text-mamba-navy">
                        <div className="w-10 h-10 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/30 rounded-full flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-mamba-sky-blue" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-mamba-navy/70 font-semibold font-montserrat uppercase tracking-wider">
                            Start Date
                          </p>
                          <p className="font-bold font-inter text-mamba-navy">
                            {new Date(adventure?.start_date).toLocaleDateString(
                              "en-US",
                              {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </p>
                        </div>
                      </div>
                      {adventure?.start_date !== adventure?.end_date && (
                        <div className="flex items-center gap-3 text-mamba-navy">
                          <div className="w-10 h-10 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/30 rounded-full flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-mamba-sky-blue" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-mamba-navy/70 font-semibold font-montserrat uppercase tracking-wider">
                              End Date
                            </p>
                            <p className="font-bold font-inter text-mamba-navy">
                              {new Date(adventure?.end_date).toLocaleDateString(
                                "en-US",
                                {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-mamba-navy">
                        <div className="w-10 h-10 bg-gradient-to-br from-mamba-golden/20 to-mamba-golden/30 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-mamba-golden" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-mamba-navy/70 font-semibold font-montserrat uppercase tracking-wider">
                            Duration
                          </p>
                          <p className="font-bold font-inter text-mamba-navy">
                            {adventure?.destination_data?.duration} <span className="text-sm font-normal text-mamba-navy/70">{adventure?.destination_data?.duration_units}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-mamba-navy">
                        <div className="w-10 h-10 bg-gradient-to-br from-mamba-navy/20 to-mamba-navy/30 rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-mamba-navy" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-mamba-navy/70 font-semibold font-montserrat uppercase tracking-wider">
                            Location
                          </p>
                          <p className="font-bold font-inter text-mamba-navy">
                            {adventure?.destination_data?.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Packages Section */}
                  {adventure?.price_tiers && (
                    <div className="border-t-2 border-slate-100">
                      <button
                        onClick={() => toggleSection("charges")}
                        className="w-full p-6 bg-gradient-to-br from-mamba-bg-light to-white hover:from-mamba-sky-blue/10 hover:to-mamba-sky-blue/20 transition-all text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-mamba-golden to-amber-400 rounded-full flex items-center justify-center ring-2 ring-mamba-navy/10">
                              <Backpack className="w-5 h-5 text-mamba-navy" />
                            </div>
                            <div>
                              <h4 className="font-black font-montserrat text-mamba-navy">
                                Choose Your Package
                              </h4>
                              <p className="text-xs text-mamba-navy/70">
                                Select a Mamba Hikers package
                              </p>
                            </div>
                          </div>
                          <motion.div
                            animate={{
                              rotate: expandedSections.charges ? 180 : 0,
                            }}
                            transition={{ duration: 0.3 }}
                            className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md"
                          >
                            <ChevronDown className="w-5 h-5 text-slate-600" />
                          </motion.div>
                        </div>
                      </button>

                      <AnimatePresence>
                        {expandedSections.charges && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 pt-0 bg-white">
                              <div className="space-y-3">
                                <PriceGridCards
                                  data={adventure?.price_tiers}
                                  theme="mamba"
                                />
                              </div>

                              {/* Pricing Includes Info - Popup Trigger */}
                              {adventure?.pricing_includes && (
                                <button
                                  onClick={() => setShowPricingModal(true)}
className="mt-6 w-full p-4 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/10 rounded-xl border-2 border-mamba-sky-blue/40 hover:border-mamba-sky-blue hover:shadow-lg hover:from-mamba-sky-blue/30 hover:to-mamba-sky-blue/15 transition-all flex items-center justify-between group shadow-sm"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                                      <ListChecks className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-base font-black font-montserrat text-mamba-navy">
                                      What's Included
                                    </span>
                                  </div>
                                  <div className="w-8 h-8 bg-mamba-sky-blue/20 rounded-full flex items-center justify-center shadow-sm group-hover:bg-mamba-sky-blue/30 transition-all">
                                    <ArrowRight className="w-5 h-5 text-mamba-sky-blue" />
                                  </div>
                                </button>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Book Now Button */}
                  <div className="p-6 bg-white">
                    {adventure?.booking_link ? (
                      <a
                        href={adventure.booking_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full px-6 py-4 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-black font-montserrat rounded-lg shadow-lg hover:shadow-2xl transition-all flex items-center justify-center gap-2 uppercase tracking-wide"
                        >
                          <span>Book This Hike</span>
                          <ArrowRight className="w-5 h-5" />
                        </motion.button>
                      </a>
                    ) : (
                      <button
                        className="w-full px-6 py-4 bg-slate-200 text-slate-500 font-bold rounded-xl cursor-not-allowed"
                        disabled
                      >
                        Booking Unavailable
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Full-Width Sections Below Grid */}
          <div className="space-y-32">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 relative h-[400px] overflow-hidden"
            >
              <img
                src={Event1}
                alt="Adventure awaits"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-mamba-navy/90 via-slate-900/70 to-mamba-sky-blue/80" />
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <div className="text-center max-w-3xl">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                  >
                    <p className="text-mamba-golden font-bold font-montserrat text-sm uppercase tracking-[0.3em] mb-4">
                      Are You Ready?
                    </p>
                    <h3 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-white mb-6 leading-tight">
                      Challenge Yourself,
                      <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                        Discover Your Limits
                      </span>
                    </h3>
                    <p className="text-lg font-dancing italic text-white/90 leading-relaxed">
                      Every mountain has a story. What will yours be?
                    </p>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Stats Section - Light Gray Background with 2-Column Layout */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 px-5 md:px-10 lg:px-16 xl:px-32 py-20 bg-gradient-to-br from-mamba-bg-light to-white scroll-mt-16"
            >
              {/* Section Title - Centered */}
              <div className="mb-16 text-center">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                  <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-sky-blue">
                    Trail Specifications
                  </span>
                  <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                </div>
                <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-mamba-navy leading-tight tracking-tight">
                  Know Your
                  <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                    Challenge
                  </span>
                </h2>
              </div>

              {/* 2-Column Layout: Mountain SVG (2/3) + Details Grid (1/3) */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* LEFT COLUMN: Visual Mountain Illustration - Takes 2 columns */}
                <div className="lg:col-span-2 relative bg-gradient-to-b from-mamba-bg-light to-white rounded-3xl p-8 overflow-hidden border-2 border-mamba-sky-blue/20">
                  {/* Background decorative elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-mamba-sky-blue/10 to-mamba-golden/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-mamba-navy/10 to-mamba-sky-blue/10 rounded-full blur-3xl"></div>

                  <div className="relative">
                    {/* Mountain SVG */}
                    <svg
                      viewBox="0 0 800 500"
                      className="w-full h-auto"
                      style={{
                        backgroundColor: "#e6f0f5",
                        borderRadius: "12px",
                      }}
                    >
                      <defs>
                        {/* Sky Gradient - Mamba Navy to Sky Blue */}
                        <linearGradient
                          id="skyGrad"
                          x1="0%"
                          y1="0%"
                          x2="0%"
                          y2="100%"
                        >
                          <stop
                            offset="0%"
                            style={{ stopColor: "#c8dce8", stopOpacity: 1 }}
                          />
                          <stop
                            offset="100%"
                            style={{ stopColor: "#e6f0f5", stopOpacity: 1 }}
                          />
                        </linearGradient>

                        {/* Mountain Gradients - Mamba Navy to Sky Blue */}
                        <linearGradient
                          id="mountainGradient"
                          x1="0%"
                          y1="0%"
                          x2="0%"
                          y2="100%"
                        >
                          <stop
                            offset="0%"
                            style={{ stopColor: "#1e3a5f", stopOpacity: 1 }}
                          />
                          <stop
                            offset="70%"
                            style={{ stopColor: "#2a5278", stopOpacity: 0.9 }}
                          />
                          <stop
                            offset="100%"
                            style={{ stopColor: "#5b9bd5", stopOpacity: 0.7 }}
                          />
                        </linearGradient>

                        {/* Animations */}
                        <style>{`
                          @keyframes drift {
                            from { transform: translateX(0px); }
                            to { transform: translateX(30px); }
                          }
                          @keyframes soar {
                            0%, 100% { transform: translateY(0); }
                            50% { transform: translateY(-8px); }
                          }
                          .cloud-group { animation: drift 12s ease-in-out infinite alternate; }
                          .bird-group { animation: soar 4s ease-in-out infinite; }
                        `}</style>
                      </defs>

                      {/* Sky Background */}
                      <rect width="800" height="500" fill="url(#skyGrad)" />

                      {/* Back Mountain Range (Muted) with realistic slopes - shifted right */}
                      <motion.path
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.6 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        d="M 550 350 L 620 270 L 660 280 L 700 130 L 740 150 L 780 170 L 850 250 L 900 270 L 950 350 Z"
                        fill="#4a6a8a"
                      />

                      {/* Secondary Background Mountain with plateaus - shifted right */}
                      <motion.path
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        d="M 50 350 L 140 260 L 200 200 L 240 210 L 280 230 L 350 270 L 400 290 L 450 350 Z"
                        fill="#5a7a9a"
                      />

                      {/* Main Mountain with gradient and realistic terrain - shifted right */}
                      <motion.path
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.4 }}
                        d="M 250 350 L 350 230 L 400 240 L 450 170 L 500 100 L 550 30 L 600 100 L 650 170 L 700 180 L 750 230 L 850 350 Z"
                        fill="url(#mountainGradient)"
                        className="drop-shadow-2xl"
                      />

                      {/* Snow Cap - Detailed - shifted right */}
                      <motion.path
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        d="M 550 30 L 610 95 L 580 85 L 550 105 L 520 85 L 490 95 Z"
                        fill="#fcfdfd"
                      />

                      {/* Flat grassland/meadow at base - Light Sky Blue tint */}
                      <motion.rect
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        x="0"
                        y="350"
                        width="800"
                        height="150"
                        fill="#b8d4e8"
                      />

                      {/* Grass texture details */}
                      <motion.rect
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        x="0"
                        y="350"
                        width="800"
                        height="40"
                        fill="#a0c0d8"
                      />

                      {/* Meandering Trail Path with perspective - Golden/Navy blend */}
                      <defs>
                        <linearGradient
                          id="trailFade"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop
                            offset="0%"
                            style={{ stopColor: "#d4a574", stopOpacity: 0.6 }}
                          />
                          <stop
                            offset="80%"
                            style={{ stopColor: "#c59563", stopOpacity: 0.4 }}
                          />
                          <stop
                            offset="100%"
                            style={{ stopColor: "#c59563", stopOpacity: 0 }}
                          />
                        </linearGradient>
                      </defs>

                      {/* Main trail path as tapered polygon */}
                      <motion.path
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.7 }}
                        d="M 0 475 
                           Q 100 465 200 460 
                           Q 300 450 400 425 
                           Q 500 395 600 365 
                           L 650 358
                           L 650 362
                           Q 500 399 400 429
                           Q 300 454 200 464
                           Q 100 469 0 479
                           Z"
                        fill="url(#trailFade)"
                      />

                      {/* Trail base layer - wider at front */}
                      <motion.path
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.75 }}
                        d="M 0 470
                           Q 100 460 200 455
                           Q 300 445 400 420
                           Q 500 390 600 362
                           L 650 357
                           L 650 363
                           Q 500 394 400 424
                           Q 300 449 200 459
                           Q 100 464 0 474
                           Z"
                        fill="#c59563"
                        opacity="0.5"
                      />

                      {/* Center highlight - tapered - Golden highlight */}
                      <motion.path
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.8 }}
                        d="M 20 473
                           Q 110 463 210 458
                           Q 310 448 410 423
                           Q 510 393 610 364
                           L 645 359
                           Q 510 392 410 422
                           Q 310 447 210 457
                           Q 110 462 20 472
                           Z"
                        fill="#d4a574"
                        opacity="0.4"
                      />

                      {/* Rocks scattered on the plain */}
                      <motion.g
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.85 }}
                      >
                        {/* Rock cluster 1 - Near trail */}
                        <g transform="translate(260, 470)">
                          <ellipse cx="0" cy="0" rx="8" ry="5" fill="#7a8580" />
                          <ellipse
                            cx="-5"
                            cy="2"
                            rx="6"
                            ry="4"
                            fill="#6a7570"
                          />
                          <ellipse cx="6" cy="1" rx="5" ry="3" fill="#8a9590" />
                        </g>

                        {/* Rock 2 - Large boulder */}
                        <g transform="translate(520, 400)">
                          <ellipse
                            cx="0"
                            cy="0"
                            rx="12"
                            ry="8"
                            fill="#6a7570"
                          />
                          <ellipse
                            cx="-4"
                            cy="-2"
                            rx="8"
                            ry="6"
                            fill="#7a8580"
                            opacity="0.7"
                          />
                        </g>

                        {/* Rock 3 - Small stones */}
                        <g transform="translate(380, 445)">
                          <ellipse cx="0" cy="0" rx="5" ry="3" fill="#8a9590" />
                          <ellipse
                            cx="8"
                            cy="1"
                            rx="4"
                            ry="2.5"
                            fill="#7a8580"
                          />
                        </g>

                        {/* Rock 4 */}
                        <g transform="translate(180, 480)">
                          <ellipse cx="0" cy="0" rx="6" ry="4" fill="#6a7570" />
                          <ellipse
                            cx="-3"
                            cy="1"
                            rx="4"
                            ry="3"
                            fill="#7a8580"
                          />
                        </g>

                        {/* Rock 5 - Far distance */}
                        <g transform="translate(600, 375)">
                          <ellipse
                            cx="0"
                            cy="0"
                            rx="7"
                            ry="4"
                            fill="#8a9590"
                            opacity="0.8"
                          />
                        </g>
                      </motion.g>

                      {/* Trees scattered on the plain */}
                      <motion.g
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                      >
                        {/* Tree 1 - Pine tree near front */}
                        <g transform="translate(90, 460)">
                          {/* Trunk */}
                          <rect
                            x="-2"
                            y="0"
                            width="4"
                            height="20"
                            fill="#5a4a3a"
                          />
                          {/* Foliage - layered triangles */}
                          <path d="M 0 -5 L -10 8 L 10 8 Z" fill="#3a5a3a" />
                          <path d="M 0 2 L -9 12 L 9 12 Z" fill="#4a6a4a" />
                          <path d="M 0 8 L -8 18 L 8 18 Z" fill="#5a7a5a" />
                        </g>

                        {/* Tree 2 - Deciduous tree right side */}
                        <g transform="translate(480, 410)">
                          {/* Trunk */}
                          <rect
                            x="-2.5"
                            y="0"
                            width="5"
                            height="18"
                            fill="#6a5a4a"
                          />
                          {/* Canopy - clusters of circles */}
                          <circle cx="0" cy="-2" r="10" fill="#4a6a4a" />
                          <circle cx="-8" cy="2" r="8" fill="#5a7a5a" />
                          <circle cx="8" cy="2" r="8" fill="#5a7a5a" />
                          <circle cx="0" cy="6" r="9" fill="#6a8a6a" />
                        </g>

                        {/* Tree 3 - Small pine */}
                        <g transform="translate(320, 440) scale(0.7)">
                          <rect
                            x="-1.5"
                            y="0"
                            width="3"
                            height="15"
                            fill="#5a4a3a"
                          />
                          <path d="M 0 -3 L -8 7 L 8 7 Z" fill="#3a5a3a" />
                          <path d="M 0 3 L -7 11 L 7 11 Z" fill="#4a6a4a" />
                        </g>

                        {/* Tree 4 - Distant tree */}
                        <g transform="translate(570, 385) scale(0.6)">
                          <rect
                            x="-2"
                            y="0"
                            width="4"
                            height="18"
                            fill="#5a4a3a"
                          />
                          <circle
                            cx="0"
                            cy="0"
                            r="10"
                            fill="#4a6a4a"
                            opacity="0.8"
                          />
                          <circle
                            cx="-6"
                            cy="3"
                            r="7"
                            fill="#5a7a5a"
                            opacity="0.8"
                          />
                          <circle
                            cx="6"
                            cy="3"
                            r="7"
                            fill="#5a7a5a"
                            opacity="0.8"
                          />
                        </g>

                        {/* Tree 5 - Large pine back right */}
                        <g transform="translate(650, 375) scale(0.5)">
                          <rect
                            x="-2"
                            y="0"
                            width="4"
                            height="20"
                            fill="#5a4a3a"
                          />
                          <path
                            d="M 0 -5 L -10 8 L 10 8 Z"
                            fill="#3a5a3a"
                            opacity="0.7"
                          />
                          <path
                            d="M 0 2 L -9 12 L 9 12 Z"
                            fill="#4a6a4a"
                            opacity="0.7"
                          />
                        </g>
                      </motion.g>

                      {/* Bushes along the trail */}
                      <motion.g
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                      >
                        {/* Bush 1 - Right side */}
                        <g transform="translate(280, 435)">
                          <circle cx="0" cy="0" r="8" fill="#5b705b" />
                          <circle cx="-6" cy="2" r="6" fill="#4a5f4a" />
                          <circle cx="6" cy="3" r="7" fill="#6a7f6a" />
                        </g>

                        {/* Bush 2 - Left side */}
                        <g transform="translate(120, 465)">
                          <circle cx="0" cy="0" r="10" fill="#5b705b" />
                          <circle cx="-7" cy="2" r="7" fill="#4a5f4a" />
                          <circle cx="7" cy="1" r="8" fill="#6a7f6a" />
                        </g>

                        {/* Bush 3 - Right side */}
                        <g transform="translate(420, 415)">
                          <circle cx="0" cy="0" r="9" fill="#5b705b" />
                          <circle cx="-6" cy="3" r="6" fill="#4a5f4a" />
                          <circle cx="6" cy="2" r="7" fill="#6a7f6a" />
                          <circle cx="0" cy="-4" r="5" fill="#6a7f6a" />
                        </g>

                        {/* Bush 4 - Left side */}
                        <g transform="translate(340, 450)">
                          <circle cx="0" cy="0" r="7" fill="#5b705b" />
                          <circle cx="-5" cy="1" r="5" fill="#4a5f4a" />
                          <circle cx="5" cy="2" r="6" fill="#6a7f6a" />
                        </g>

                        {/* Bush 5 - Right side */}
                        <g transform="translate(550, 385)">
                          <circle cx="0" cy="0" r="11" fill="#5b705b" />
                          <circle cx="-8" cy="2" r="7" fill="#4a5f4a" />
                          <circle cx="8" cy="3" r="8" fill="#6a7f6a" />
                          <circle cx="0" cy="-5" r="6" fill="#6a7f6a" />
                        </g>

                        {/* Bush 6 - Additional */}
                        <g transform="translate(450, 440)">
                          <circle cx="0" cy="0" r="6" fill="#5b705b" />
                          <circle cx="-4" cy="2" r="5" fill="#4a5f4a" />
                          <circle cx="4" cy="1" r="5" fill="#6a7f6a" />
                        </g>

                        {/* Bush 7 - Left foreground */}
                        <g transform="translate(50, 480)">
                          <circle cx="0" cy="0" r="9" fill="#5b705b" />
                          <circle cx="-6" cy="1" r="6" fill="#4a5f4a" />
                          <circle cx="6" cy="2" r="7" fill="#6a7f6a" />
                        </g>

                        {/* Bush 8 - Far right */}
                        <g transform="translate(620, 380)" opacity="0.8">
                          <circle cx="0" cy="0" r="7" fill="#5b705b" />
                          <circle cx="-5" cy="2" r="5" fill="#4a5f4a" />
                          <circle cx="5" cy="1" r="6" fill="#6a7f6a" />
                        </g>
                      </motion.g>

                      {/* Animals along the trail */}
                      <motion.g
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                      >
                        {/* Deer - Right side near bush */}
                        <g transform="translate(460, 410)">
                          {/* Body */}
                          <ellipse cx="0" cy="0" rx="8" ry="5" fill="#7a5f4a" />
                          {/* Head and neck */}
                          <ellipse
                            cx="-8"
                            cy="-3"
                            rx="4"
                            ry="3"
                            fill="#8a6f5a"
                          />
                          {/* Legs */}
                          <line
                            x1="-3"
                            y1="5"
                            x2="-3"
                            y2="12"
                            stroke="#6a4f3a"
                            strokeWidth="1.5"
                          />
                          <line
                            x1="3"
                            y1="5"
                            x2="3"
                            y2="12"
                            stroke="#6a4f3a"
                            strokeWidth="1.5"
                          />
                          {/* Antlers */}
                          <path
                            d="M -10 -6 L -11 -10 M -10 -6 L -9 -10"
                            stroke="#5a4a3a"
                            strokeWidth="1"
                            fill="none"
                          />
                        </g>

                        {/* Rabbit - Left side */}
                        <g transform="translate(300, 458)">
                          {/* Body */}
                          <ellipse cx="0" cy="0" rx="5" ry="4" fill="#8a7a6a" />
                          {/* Head */}
                          <circle cx="-4" cy="-2" r="3" fill="#9a8a7a" />
                          {/* Ears */}
                          <ellipse
                            cx="-5"
                            cy="-5"
                            rx="1.5"
                            ry="4"
                            fill="#8a7a6a"
                          />
                          <ellipse
                            cx="-3"
                            cy="-5"
                            rx="1.5"
                            ry="4"
                            fill="#8a7a6a"
                          />
                          {/* Tail */}
                          <circle
                            cx="5"
                            cy="0"
                            r="2"
                            fill="#ffffff"
                            opacity="0.8"
                          />
                        </g>

                        {/* Bird on ground - Left side */}
                        <g transform="translate(380, 455)">
                          {/* Body */}
                          <ellipse
                            cx="0"
                            cy="0"
                            rx="3"
                            ry="2.5"
                            fill="#5a4a3a"
                          />
                          {/* Head */}
                          <circle cx="-2.5" cy="-1" r="1.5" fill="#6a5a4a" />
                          {/* Beak */}
                          <path
                            d="M -4 -1 L -5.5 -0.8"
                            stroke="#4a3a2a"
                            strokeWidth="0.8"
                          />
                          {/* Tail */}
                          <path d="M 3 0 L 5 -1 L 5 1 Z" fill="#5a4a3a" />
                        </g>
                      </motion.g>

                      {/* Mist/Fog at mountain base */}
                      <motion.rect
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.3 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        x="0"
                        y="300"
                        width="800"
                        height="60"
                        fill="white"
                      />

                      {/* Hikers - Group of 3 approaching from left */}
                      <motion.g
                        initial={{ x: -80, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 1.5, delay: 1 }}
                      >
                        {/* Hiker 1 - Lead (at front heading right toward mountain) */}
                        <g
                          transform="translate(220, 445) scale(1.2)"
                          fill="#2d382d"
                        >
                          {/* Legs */}
                          <path
                            d="M 0 0 L -2 15 L -6 30 M 0 0 L 4 15 L 8 30"
                            stroke="#2d382d"
                            strokeWidth="3"
                            fill="none"
                            strokeLinecap="round"
                          />
                          {/* Torso */}
                          <path
                            d="M 0 -15 L 2 0 L 0 15"
                            stroke="#2d382d"
                            strokeWidth="5"
                            fill="none"
                            strokeLinecap="round"
                          />
                          {/* Head */}
                          <circle cx="2" cy="-22" r="4" fill="#2d382d" />
                          {/* Backpack */}
                          <rect
                            x="-6"
                            y="-15"
                            width="6"
                            height="12"
                            rx="2"
                            fill="#2d382d"
                          />
                          {/* Walking Stick */}
                          <line
                            x1="8"
                            y1="-5"
                            x2="12"
                            y2="30"
                            stroke="#2d382d"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </g>

                        {/* Hiker 2 - Following */}
                        <g
                          transform="translate(165, 448) scale(1.1)"
                          fill="#3d4a3d"
                        >
                          {/* Legs */}
                          <path
                            d="M 0 0 L -2 15 L -5 28 M 0 0 L 3 15 L 6 28"
                            stroke="#3d4a3d"
                            strokeWidth="3"
                            fill="none"
                            strokeLinecap="round"
                          />
                          {/* Torso */}
                          <path
                            d="M -1 -15 L 1 0 L -1 15"
                            stroke="#3d4a3d"
                            strokeWidth="5"
                            fill="none"
                            strokeLinecap="round"
                          />
                          {/* Head */}
                          <circle cx="1" cy="-22" r="4" fill="#3d4a3d" />
                          {/* Backpack */}
                          <rect
                            x="-7"
                            y="-16"
                            width="7"
                            height="13"
                            rx="2"
                            fill="#3d4a3d"
                          />
                          {/* Walking Stick */}
                          <line
                            x1="7"
                            y1="-5"
                            x2="10"
                            y2="28"
                            stroke="#3d4a3d"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </g>

                        {/* Hiker 3 - Rear (furthest left) */}
                        <g
                          transform="translate(110, 450) scale(1)"
                          fill="#4a5a4a"
                        >
                          {/* Legs */}
                          <path
                            d="M 0 0 L -2 15 L -5 28 M 0 0 L 3 15 L 6 28"
                            stroke="#4a5a4a"
                            strokeWidth="3"
                            fill="none"
                            strokeLinecap="round"
                          />
                          {/* Torso */}
                          <path
                            d="M -1 -15 L 1 0 L -1 15"
                            stroke="#4a5a4a"
                            strokeWidth="5"
                            fill="none"
                            strokeLinecap="round"
                          />
                          {/* Head */}
                          <circle cx="1" cy="-22" r="4" fill="#4a5a4a" />
                          {/* Backpack */}
                          <rect
                            x="-7"
                            y="-16"
                            width="7"
                            height="13"
                            rx="2"
                            fill="#4a5a4a"
                          />
                          {/* Walking Stick */}
                          <line
                            x1="7"
                            y1="-5"
                            x2="10"
                            y2="28"
                            stroke="#4a5a4a"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </g>
                      </motion.g>

                      {/* Animated Clouds */}
                      <g className="cloud-group">
                        <motion.g
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.8 }}
                          transition={{ duration: 0.8, delay: 1.4 }}
                          fill="#ffffff"
                        >
                          <circle cx="150" cy="120" r="25" />
                          <circle cx="180" cy="120" r="35" />
                          <circle cx="215" cy="120" r="25" />
                        </motion.g>
                        <motion.g
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.5 }}
                          transition={{ duration: 0.8, delay: 1.5 }}
                          fill="#ffffff"
                        >
                          <circle cx="600" cy="80" r="20" />
                          <circle cx="625" cy="80" r="28" />
                        </motion.g>
                      </g>

                      {/* Birds - Minimalist V-shapes */}
                      <g className="bird-group">
                        <motion.path
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.6, delay: 1.6 }}
                          d="M 520 120 Q 525 115 530 120 Q 535 115 540 120"
                          fill="none"
                          stroke="#2f3e33"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <motion.path
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.7 }}
                          transition={{ duration: 0.6, delay: 1.7 }}
                          d="M 550 140 Q 555 135 560 140 Q 565 135 570 140"
                          fill="none"
                          stroke="#2f3e33"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <motion.path
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.6 }}
                          transition={{ duration: 0.6, delay: 1.8 }}
                          d="M 490 145 Q 495 140 500 145 Q 505 140 510 145"
                          fill="none"
                          stroke="#2f3e33"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </g>
                    </svg>

                    {/* Interactive Data Points on Mountain */}
                    <div className="absolute inset-0">
                      {/* Max Elevation - Peak (adjusted for mountain shift) */}
                      <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 1.9 }}
                        className="absolute top-[6%] left-[68%] transform -translate-x-1/2"
                      >
                        <div className="relative">
                          <div className="absolute -top-2 -left-2 w-6 h-6 bg-mamba-sky-blue rounded-full animate-ping opacity-75"></div>
                          <div className="relative w-6 h-6 bg-mamba-sky-blue rounded-full border-3 border-white shadow-xl flex items-center justify-center">
                            <Mountain className="w-3 h-3 text-white" />
                          </div>
                          <div className="absolute top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                            <div className="bg-gradient-to-br from-mamba-sky-blue to-mamba-navy text-white px-4 py-3 rounded-xl shadow-2xl border-2 border-white">
                              <div className="flex items-center gap-2 mb-1">
                                <Mountain className="w-4 h-4" />
                                <div className="text-xs font-bold uppercase tracking-wider">
                                  Max Elevation
                                </div>
                              </div>
                              <div className="text-2xl font-black">
                                {adventure?.destination_data?.max_elevation}
                                <span className="text-sm font-semibold opacity-70 ml-1">
                                  {adventure?.destination_data?.elevation_unit}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Elevation Gain - Slope (adjusted) */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 2.1 }}
                        className="absolute top-[32%] left-[48%]"
                      >
                        <div className="relative">
                          <div className="absolute -top-2 -left-2 w-6 h-6 bg-mamba-golden rounded-full animate-ping opacity-75"></div>
                          <div className="relative w-6 h-6 bg-mamba-golden rounded-full border-3 border-white shadow-xl flex items-center justify-center">
                            <TrendingUp className="w-3 h-3 text-white" />
                          </div>
                          <div className="absolute top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                            <div className="bg-gradient-to-br from-mamba-golden to-amber-500 text-mamba-navy px-4 py-3 rounded-xl shadow-2xl border-2 border-white">
                              <div className="flex items-center gap-2 mb-1">
                                <TrendingUp className="w-4 h-4" />
                                <div className="text-xs font-bold uppercase tracking-wider">
                                  Elevation Gain
                                </div>
                              </div>
                              <div className="text-2xl font-black">
                                {adventure?.destination_data?.elevation_gain}
                                <span className="text-sm font-semibold opacity-70 ml-1">
                                  {adventure?.destination_data?.elevation_unit}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Distance - Trail in foreground */}
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 2.3 }}
                        className="absolute bottom-[8%] left-[15%]"
                      >
                        <div className="relative">
                          <div className="absolute -top-2 -left-2 w-6 h-6 bg-mamba-navy rounded-full animate-ping opacity-75"></div>
                          <div className="relative w-6 h-6 bg-mamba-navy rounded-full border-3 border-white shadow-xl flex items-center justify-center">
                            <Navigation className="w-3 h-3 text-white" />
                          </div>
                          <div className="absolute top-10 left-1/2 transform -translate-x-1/2">
                            <div className="bg-gradient-to-br from-mamba-navy to-mamba-sky-blue text-white px-4 py-3 rounded-xl shadow-2xl border-2 border-white">
                              <div className="flex items-center gap-2 mb-1">
                                <Navigation className="w-4 h-4" />
                                <div className="text-xs font-bold uppercase tracking-wider">
                                  Total Distance
                                </div>
                              </div>
                              <div className="text-2xl font-black">
                                {adventure?.destination_data?.distance_in_km}
                                <span className="text-sm font-semibold opacity-70 ml-1">KM</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="relative mt-8 flex flex-wrap gap-4 justify-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-mamba-sky-blue rounded-full"></div>
                      <span className="text-xs font-semibold font-montserrat text-mamba-navy">
                        Max Elevation
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-mamba-golden rounded-full"></div>
                      <span className="text-xs font-semibold font-montserrat text-mamba-navy">
                        Elevation Gain
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-mamba-navy rounded-full"></div>
                      <span className="text-xs font-semibold font-montserrat text-mamba-navy">
                        Distance
                      </span>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN: Details Grid - Takes 1 column */}
                <div className="space-y-6">
                  {[
                    {
                      icon: Navigation,
                      label: "Distance",
                      value: adventure?.destination_data?.distance_in_km,
                      unit: "KM",
                      color: "cyan",
                    },
                    {
                      icon: Mountain,
                      label: "Max Elevation",
                      value: adventure?.destination_data?.max_elevation,
                      unit: adventure?.destination_data?.elevation_unit,
                      color: "emerald",
                    },
                    {
                      icon: TrendingUp,
                      label: "Elevation Gain",
                      value: adventure?.destination_data?.elevation_gain,
                      unit: adventure?.destination_data?.elevation_unit,
                      color: "teal",
                    },
                    {
                      icon: Clock,
                      label: "Duration",
                      value: adventure?.destination_data?.duration,
                      unit: adventure?.destination_data?.duration_units,
                      color: "blue",
                    },

                    {
                      icon: MapPin,
                      label: "Country",
                      value: adventure?.destination_data?.country,
                      unit: null,
                      color: "purple",
                    },
                    {
                      icon: Shield,
                      label: "Difficulty",
                      value: adventure?.destination_data?.difficulty,
                      unit: null,
                      color: "orange",
                    },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      whileHover={{ scale: 1.02 }}
                      className="relative bg-white rounded-2xl p-6 border-2 border-mamba-sky-blue/20 hover:border-mamba-sky-blue hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <div className="inline-flex p-4 bg-gradient-to-br from-mamba-sky-blue/10 to-mamba-sky-blue/20 rounded-xl border border-mamba-sky-blue/30">
                            <stat.icon className="w-7 h-7 text-mamba-sky-blue" />
                          </div>
                        </div>
                        <div className="flex-grow">
                          <p className="text-xs font-bold font-montserrat text-mamba-navy/70 uppercase tracking-wider mb-1">
                            {stat.label}
                          </p>
                          <p className="text-2xl lg:text-3xl font-black font-inter text-mamba-navy">
                            {stat.value}
                            {stat.unit && (
                              <span className="text-sm lg:text-base font-semibold text-mamba-navy/60 ml-1">
                                {stat.unit}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-mamba-sky-blue to-mamba-navy rounded-l-2xl" />
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Itinerary Section - Timeline Design with Background Image */}
            {adventure?.itinerary_entries_data &&
              adventure.itinerary_entries_data.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 px-5 md:px-10 lg:px-16 xl:px-32 py-20 relative scroll-mt-16"
                  style={{
                    backgroundImage:
                      "url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&q=80)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: "fixed",
                  }}
                >
                  {/* Overlay - Higher opacity for better readability */}
                  <div className="absolute inset-0 bg-white/95 backdrop-blur-sm"></div>

                  <div className="relative z-10">
                    {/* 2-Column Layout: Timeline + Map */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Timeline Container - Left Column */}
                      <div className="relative">
                        {/* Section Title - Left Aligned */}
                        <div className="mb-12">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="h-px w-12 bg-gradient-to-r from-mamba-sky-blue to-transparent" />
                            <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-sky-blue">
                              Day by Day
                            </span>
                          </div>
                          <h2 className="text-3xl lg:text-4xl font-black font-montserrat text-mamba-navy leading-tight tracking-tight">
                            Your Complete
                            <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                              Journey Map
                            </span>
                          </h2>
                        </div>

                        {/* Timeline */}
                        {/* Dashed Trail Line */}
                        <div className="absolute left-[31px] top-0 bottom-0 w-0.5 border-l-[3px] border-dashed border-mamba-sky-blue/40" />

                        {/* Group entries by day */}
                        {Object.entries(
                          adventure.itinerary_entries_data
                            .filter(
                              (entry) => entry.is_active && !entry.is_deleted,
                            )
                            .sort((a, b) => {
                              if (a.day !== b.day) return a.day - b.day;
                              return a.order - b.order;
                            })
                            .reduce((acc, entry) => {
                              const day = entry.day;
                              if (!acc[day]) acc[day] = [];
                              acc[day].push(entry);
                              return acc;
                            }, {}),
                        ).map(([day, entries]) => (
                          <div key={day} className="mb-8">
                            {/* Day Label */}
                            <motion.div
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              className="relative z-10 mb-6"
                            >
                              <span className="inline-block bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white px-5 py-2 rounded-full font-black font-montserrat text-sm shadow-lg">
                                DAY {String(day).padStart(2, "0")}
                              </span>
                            </motion.div>

                            {/* Timeline Items for this day */}
                            {entries.map((entry, index) => (
                              <motion.div
                                key={entry.id}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="relative mb-10 pl-20"
                              >
                                {/* Timeline Dot */}
                                <div className="absolute left-[21px] top-1.5 w-[22px] h-[22px] bg-white border-4 border-mamba-sky-blue rounded-full z-20 shadow-md" />

                                {/* Time Stamp */}
                                {entry.time && (
                                  <span className="block font-black font-montserrat text-mamba-sky-blue text-sm mb-2 uppercase tracking-wider">
                                    {new Date(
                                      `1970-01-01T${entry.time}`,
                                    ).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}
                                  </span>
                                )}

                                {/* Content Card - Semi-transparent to show background */}
                                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 shadow-md border-b-4 border-transparent hover:border-mamba-sky-blue transition-all hover:-translate-y-1 hover:bg-white/90">
                                  <h3 className="text-xl font-black font-montserrat text-mamba-navy mb-3">
                                    {entry.title}
                                  </h3>
                                  {entry.description && (
                                    <div className="prose prose-sm max-w-none text-slate-600">
                                      <HtmlContent
                                        htmlData={entry.description}
                                      />
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        ))}
                      </div>

                      {/* Map Container - Right Column with Wavy Border */}
                      <div className="relative h-full">
                        <motion.div
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8 }}
                          className="sticky top-8 h-[600px]"
                        >
                          {/* Wavy Image with CTA */}
                          <div className="relative w-full h-full">
                            {/* Mountain Image with Wavy Left Border on large screens, rounded border on small */}
                            <div
                              className="w-full h-full relative rounded-2xl lg:rounded-none overflow-hidden"
                              style={{
                                clipPath: "var(--clip-path-wavy)",
                              }}
                            >
                              <style>{`
                            @media (min-width: 1024px) {
                              :root {
                                --clip-path-wavy: url(#wavyLeft);
                              }
                            }
                            @media (max-width: 1023px) {
                              :root {
                                --clip-path-wavy: none;
                              }
                            }
                          `}</style>
                              <img
                                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80"
                                alt="Mountain adventure"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/85 to-slate-900/90" />

                              {/* Contact CTA - More Prominent */}
                              <div className="absolute inset-0 flex items-center justify-center p-6 md:p-8">
                                <div className="bg-white rounded-3xl p-8 md:p-10 max-w-lg w-full shadow-2xl border-4 border-mamba-golden/30">
                                  {/* Badge */}
                                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mamba-golden/20 border border-mamba-golden/40 mb-4">
                                    <Sparkles className="w-4 h-4 text-mamba-golden" />
                                    <span className="text-xs font-black uppercase tracking-wider text-mamba-navy">
                                      We're Here to Help
                                    </span>
                                  </div>

                                  {/* Heading */}
                                  <h3 className="text-3xl lg:text-4xl font-black font-montserrat text-mamba-navy leading-tight mb-3">
                                    Talk to a
                                    <span className="block text-mamba-sky-blue">
                                      Mamba Guide
                                    </span>
                                  </h3>

                                  {/* Description */}
                                  <p className="text-base text-slate-600 mb-6 leading-relaxed">
                                    Route advice, gear checks, and bookings.
                                    Reach out and we'll guide you through every
                                    step.
                                  </p>

                                  {/* Contact Buttons - Small, in one row */}
                                  <div className="flex gap-3">
                                    <a
                                      href="tel:+254700000000"
                                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                                    >
                                      <Phone className="w-4 h-4" />
                                      Call
                                    </a>
                                    <a
                                      href="https://wa.me/254700000000"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                      WhatsApp
                                    </a>
                                    <a
                                      href="mailto:info@mambahikers.com?subject=Trail%20Inquiry"
                                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-mamba-navy text-white font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                                    >
                                      <Mail className="w-4 h-4" />
                                      Email
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Hidden SVG for Clip Path */}
                            <svg
                              width="0"
                              height="0"
                              style={{ position: "absolute" }}
                            >
                              <defs>
                                <clipPath
                                  id="wavyLeft"
                                  clipPathUnits="objectBoundingBox"
                                >
                                  <path d="M1,0 L0.2,0 C0.05,0.15 0.35,0.25 0.2,0.45 C0.05,0.65 0.35,0.75 0.2,1 L1,1 Z" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

            {/* Preparation Banner - Between Itinerary and What to Wear */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 relative h-[350px] overflow-hidden"
            >
              <img
                src={Event2}
                alt="Preparation"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/90 via-mamba-sky-blue/70 to-slate-900/85" />
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <div className="text-center max-w-4xl">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-col md:flex-row items-center justify-center gap-8"
                  >
                    <div className="text-left md:text-right flex-1">
                      <h3 className="text-3xl md:text-4xl font-black font-montserrat text-white mb-3 leading-tight">
                        Gear Up for
                        <span className="block text-mamba-golden">Success</span>
                      </h3>
                      <p className="text-slate-200 text-lg">
                        The right preparation makes all the difference
                      </p>
                    </div>
                    <div className="h-16 w-px bg-emerald-400/30 hidden md:block"></div>
                    <div className="text-left flex-1">
                      <div className="inline-flex items-center gap-3 mb-3">
                        <CheckCircle2 className="w-6 h-6 text-mamba-golden" />
                        <span className="text-white font-semibold">
                          Quality Equipment
                        </span>
                      </div>
                      <div className="inline-flex items-center gap-3">
                        <CheckCircle2 className="w-6 h-6 text-mamba-golden" />
                        <span className="text-white font-semibold">
                          Expert Guidance
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* What to Wear Section - Split Design */}
            {adventure?.what_to_wear_data &&
              adventure.what_to_wear_data.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 scroll-mt-16"
                >
                  <div className="grid lg:grid-cols-2">
                    {/* Left: Content & Products */}
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                      className="flex items-center justify-center p-8 lg:p-16 bg-gradient-to-br from-mamba-bg-light to-white"
                    >
                      <div className="max-w-2xl w-full">
                        <div className="inline-block mb-6 px-4 py-2 bg-mamba-sky-blue/20 rounded-full">
                          <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-sky-blue">
                            What to Wear
                          </span>
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-mamba-navy mb-6 leading-tight">
                          Dress for
                          <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                            The Journey
                          </span>
                        </h2>
                        <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                          Essential clothing to keep you comfortable and
                          protected throughout your adventure.
                        </p>

                        {/* Product List - Compact */}
                        <div className="space-y-3 mb-8">
                          {adventure.what_to_wear_data
                            .filter(
                              (item) => item.is_active && !item.is_deleted,
                            )
                            .sort((a, b) => a.order - b.order)
                            .slice(0, 6)
                            .map((item, index) => (
                              <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.05 }}
                                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-mamba-sky-blue/20 hover:border-mamba-sky-blue hover:shadow-md transition-all"
                              >
                                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-mamba-sky-blue/10 to-mamba-sky-blue/20 rounded-lg flex items-center justify-center">
                                  {item.is_required ? (
                                    <span className="text-red-600 font-black text-sm">
                                      !
                                    </span>
                                  ) : (
                                    <CheckCircle2 className="w-5 h-5 text-mamba-sky-blue" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-bold font-inter text-mamba-navy">
                                    {item.product.name}
                                  </h4>
                                </div>
                                {item.is_required && (
                                  <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                                    Required
                                  </span>
                                )}
                              </motion.div>
                            ))}
                        </div>

                        {/* Shop Button */}
                        <a
                          href="https://www.instagram.com/mamba_hikers/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-bold font-montserrat rounded-full hover:shadow-2xl transition-all hover:scale-105"
                        >
                          <ExternalLink className="w-5 h-5" />
                          Go to Shop
                          <ArrowRight className="w-5 h-5" />
                        </a>
                      </div>
                    </motion.div>

                    {/* Right: Image */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                      className="relative h-[400px] lg:h-auto"
                    >
                      <img
                        src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&q=90"
                        alt="Hiking gear"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </motion.div>
                  </div>
                </motion.div>
              )}

            {/* How to Prepare Section */}
            {adventure?.preparations_data &&
              adventure.preparations_data.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 px-5 md:px-10 lg:px-16 xl:px-32 py-20 bg-gradient-to-br from-mamba-bg-light via-white to-mamba-bg-light scroll-mt-16"
                >
                  {/* Section Header */}
                  <div className="mb-16 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                      <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-sky-blue">
                        Get Ready
                      </span>
                      <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-mamba-navy leading-tight tracking-tight">
                      How to Prepare
                      <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                        For The Hike
                      </span>
                    </h2>
                    <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto font-inter">
                      Follow these recommendations to ensure you're physically and mentally ready for your adventure
                    </p>
                  </div>

                  {/* Two Column Layout - Preparations + Image */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
                    {/* LEFT: Preparations Grid - Takes 3 columns */}
                    <div className="lg:col-span-3">
                      {(() => {
                        // Group preparations by category
                        const groupedPreparations = adventure.preparations_data
                          .filter((item) => item.is_active && !item.is_deleted)
                          .sort((a, b) => a.order - b.order)
                          .reduce((acc, item) => {
                            const category = item.category || "General";
                            if (!acc[category]) acc[category] = [];
                            acc[category].push(item);
                            return acc;
                          }, {});

                        // Category icon mapping
                        const getCategoryIcon = (category) => {
                          switch (category?.toLowerCase()) {
                            case "exercise":
                              return Dumbbell;
                            case "dos and donts":
                            case "dos and don'ts":
                              return ListChecks;
                            case "nutrition":
                              return Utensils;
                            case "cancellation policy":
                              return Shield;
                            case "gear check":
                              return Backpack;
                            case "others":
                            default:
                              return CheckCircle2;
                          }
                        };

                        // Category color mapping
                        const getCategoryColor = (category) => {
                          switch (category?.toLowerCase()) {
                            case "exercise":
                              return {
                                bg: "from-orange-500 to-red-500",
                                light: "from-orange-500/10 to-red-500/10",
                                border: "border-orange-500/30",
                                text: "text-orange-600",
                              };
                            case "dos and donts":
                            case "dos and don'ts":
                              return {
                                bg: "from-green-500 to-emerald-500",
                                light: "from-green-500/10 to-emerald-500/10",
                                border: "border-green-500/30",
                                text: "text-green-600",
                              };
                            case "nutrition":
                              return {
                                bg: "from-amber-500 to-yellow-500",
                                light: "from-amber-500/10 to-yellow-500/10",
                                border: "border-amber-500/30",
                                text: "text-amber-600",
                              };
                            case "cancellation policy":
                              return {
                                bg: "from-rose-500 to-pink-500",
                                light: "from-rose-500/10 to-pink-500/10",
                                border: "border-rose-500/30",
                                text: "text-rose-600",
                              };
                            case "gear check":
                              return {
                                bg: "from-mamba-sky-blue to-blue-500",
                                light: "from-mamba-sky-blue/10 to-blue-500/10",
                                border: "border-mamba-sky-blue/30",
                                text: "text-mamba-sky-blue",
                              };
                            case "others":
                            default:
                              return {
                                bg: "from-mamba-navy to-slate-700",
                                light: "from-mamba-navy/10 to-slate-700/10",
                                border: "border-mamba-navy/30",
                                text: "text-mamba-navy",
                              };
                          }
                        };

                        return (
                          <div className="space-y-10">
                            {Object.entries(groupedPreparations).map(
                              ([category, items], categoryIndex) => {
                                const CategoryIcon = getCategoryIcon(category);
                                const colors = getCategoryColor(category);

                                return (
                                  <motion.div
                                    key={category}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: categoryIndex * 0.1 }}
                                  >
                                    {/* Category Header */}
                                    <div className="flex items-center gap-4 mb-5">
                                      <div
                                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-lg`}
                                      >
                                        <CategoryIcon className="w-6 h-6 text-white" />
                                      </div>
                                      <div>
                                        <h3 className="text-xl font-black font-montserrat text-mamba-navy">
                                          {category}
                                        </h3>
                                        <p className="text-sm text-slate-500">
                                          {items.length} {items.length === 1 ? "tip" : "tips"}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Category Items */}
                                    <div className="space-y-3">
                                      {items.map((item, index) => (
                                        <motion.div
                                          key={item.id}
                                          initial={{ opacity: 0, x: -20 }}
                                          whileInView={{ opacity: 1, x: 0 }}
                                          viewport={{ once: true }}
                                          transition={{ delay: index * 0.05 }}
                                          whileHover={{ x: 4 }}
                                          className={`relative bg-white rounded-xl p-5 border-2 ${colors.border} hover:border-opacity-60 shadow-sm hover:shadow-lg transition-all overflow-hidden group`}
                                        >
                                          {/* Content */}
                                          <div className="relative">
                                            {/* Title with icon */}
                                            <div className="flex items-start gap-3 mb-2">
                                              <div
                                                className={`flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br ${colors.light} flex items-center justify-center`}
                                              >
                                                <CheckCircle2
                                                  className={`w-4 h-4 ${colors.text}`}
                                                />
                                              </div>
                                              <h4 className="text-base font-bold font-montserrat text-mamba-navy leading-tight">
                                                {item.title}
                                              </h4>
                                            </div>

                                            {/* Description */}
                                            {item.description && (
                                              <div className="pl-10 prose prose-sm max-w-none text-slate-600 [&_p]:mb-0">
                                                <HtmlContent htmlData={item.description} />
                                              </div>
                                            )}
                                          </div>

                                          {/* Left accent line */}
                                          <div
                                            className={`absolute top-0 left-0 w-1 h-0 bg-gradient-to-b ${colors.bg} group-hover:h-full transition-all duration-300`}
                                          />
                                        </motion.div>
                                      ))}
                                    </div>
                                  </motion.div>
                                );
                              }
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* RIGHT: Inspirational Image - Takes 2 columns */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="lg:col-span-2 lg:sticky lg:top-24"
                    >
                      <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                        {/* Main Image */}
                        <img
                          src={Prepare}
                          alt="Runner training for hiking"
                          className="w-full min-h-[250px] lg:min-h-[300px] object-cover"
                        />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/90 via-mamba-navy/20 to-transparent" />
                        
                        {/* Content Overlay */}
                        {/* <div className="absolute bottom-0 left-0 right-0 p-8">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-mamba-golden" />
                            <span className="text-sm font-bold text-mamba-golden uppercase tracking-wider">
                              Pro Tip
                            </span>
                          </div>
                          <p className="text-xl font-bold text-white font-montserrat leading-relaxed">
                            Start your preparation at least 2-4 weeks before the hike for best results.
                          </p>
                          <p className="mt-3 text-slate-300 text-sm">
                            Consistency is key. Small daily efforts lead to big improvements in your hiking performance.
                          </p>
                        </div> */}

                        {/* Decorative Corner */}
                        <div className="absolute top-4 right-4 w-16 h-16 border-t-4 border-r-4 border-mamba-golden/50 rounded-tr-2xl" />
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              )}

            {/* What to Carry Section - Dark Background */}
            {adventure?.what_to_carry_data &&
            adventure.what_to_carry_data.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 px-5 md:px-10 lg:px-16 xl:px-32 py-20 bg-gradient-to-br from-mamba-navy to-slate-900 scroll-mt-16"
              >
                <div className="mb-16 text-center">
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent" />
                    <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-golden">
                      What to Carry
                    </span>
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent" />
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-white leading-tight tracking-tight">
                    Pack Your
                    <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                      Essentials
                    </span>
                  </h2>

                  {/* Shop Button - Centered below title */}
                  <a
                    href="https://www.instagram.com/mamba_hikers/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold font-montserrat rounded-full hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg hover:shadow-xl mt-6"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>Go to Shop</span>
                  </a>
                </div>

                {/* Horizontal Product Cards - 4 per row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {adventure.what_to_carry_data
                    .filter((item) => item.is_active && !item.is_deleted)
                    .sort((a, b) => a.order - b.order)
                    .map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-5 border-2 border-mamba-sky-blue/30 hover:border-mamba-sky-blue hover:shadow-xl transition-all group"
                      >
                        {/* Icon/Status */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Backpack className="w-6 h-6 text-mamba-sky-blue" />
                          </div>
                          {item.is_required && (
                            <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                              Required
                            </span>
                          )}
                        </div>

                        {/* Product Name */}
                        <h4 className="font-black font-montserrat text-lg text-white mb-2 line-clamp-2 group-hover:text-mamba-golden transition-colors">
                          {item.product.name}
                        </h4>

                        {/* Description */}
                        {item.product.short_description && (
                          <p className="text-sm text-slate-300 line-clamp-2">
                            {item.product.short_description}
                          </p>
                        )}
                      </motion.div>
                    ))}
                </div>
              </motion.div>
            ) : adventure?.what_to_carry ? (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="-mx-5 md:-mx-10 lg:-mx-16 xl:-mx-32 px-5 md:px-10 lg:px-16 xl:px-32 py-20 bg-gradient-to-br from-mamba-navy to-slate-900 scroll-mt-16"
              >
                <div className="mb-16 text-center">
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent" />
                    <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-golden">
                      What to Carry
                    </span>
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent" />
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-black font-montserrat text-white leading-tight tracking-tight">
                    Pack Your
                    <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                      Essentials
                    </span>
                  </h2>
                </div>
                <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-6 border-2 border-mamba-sky-blue/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="inline-flex p-3 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/30 rounded-xl">
                      <Backpack className="w-5 h-5 text-mamba-sky-blue" />
                    </div>
                    <h3 className="text-lg font-bold font-montserrat text-white">
                      What to Carry
                    </h3>
                  </div>
                  <div className="prose prose-base max-w-none text-slate-300 [&_*]:text-slate-300">
                    <HtmlContent htmlData={adventure?.what_to_carry} />
                  </div>
                </div>
              </motion.div>
            ) : null}
          </div>
        </div>

        {/* More Adventures - Full Width Section */}
        {otherAdventures?.results?.length > 0 && (
          <div className="bg-gradient-to-br from-mamba-bg-light to-white py-32">
            <div className="px-5 md:px-10 lg:px-16 xl:px-32">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <div className="mb-12 text-center">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="h-px w-12 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                    <span className="text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-mamba-sky-blue">
                      Continue Exploring
                    </span>
                    <div className="h-px w-12 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent" />
                  </div>
                  <h2 className="text-3xl lg:text-5xl font-black font-montserrat text-mamba-navy leading-tight tracking-tight">
                    More Epic
                    <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                      Adventures Await
                    </span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {otherAdventures.results
                    .filter((event) => event.id !== adventure?.id)
                    .slice(0, 4)
                    .map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -8 }}
                        onClick={() => navigate(`/events/${event.slug}`)}
                        className="group bg-white rounded-3xl overflow-hidden border-2 border-mamba-sky-blue/20 hover:border-mamba-sky-blue transition-all cursor-pointer shadow-sm hover:shadow-2xl"
                      >
                        {/* Image */}
                        <div className="relative h-56 overflow-hidden">
                          <img
                            src={
                              event?.destination?.images?.[0]?.image_url ||
                              event?.display_image_url ||
                              NoImage
                            }
                            alt={event?.destination?.name || event?.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />

                          {/* Location badge */}
                          <div className="absolute top-4 left-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full">
                              <MapPin className="w-3 h-3 text-white" />
                              <span className="text-xs font-semibold text-white">
                                {event?.destination?.country}
                              </span>
                            </div>
                          </div>

                          {/* Arrow icon */}
                          <div className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <ArrowRight className="w-5 h-5 text-white" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <h4 className="font-black font-montserrat text-xl text-mamba-navy mb-3 line-clamp-2 group-hover:text-mamba-sky-blue transition-colors">
                            {event?.destination?.name}
                          </h4>

                          <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-mamba-sky-blue" />
                              <span>
                                {new Date(event.start_date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              </span>
                            </div>
                            {event?.destination?.type && (
                              <div className="flex items-center gap-1.5">
                                <Mountain className="w-4 h-4 text-mamba-sky-blue" />
                                <span>{event.destination.type}</span>
                              </div>
                            )}
                          </div>

                          <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                View Details
                              </span>
                              <div className="w-8 h-8 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/30 rounded-full flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-mamba-sky-blue group-hover:to-mamba-navy transition-all">
                                <ArrowRight className="w-4 h-4 text-mamba-sky-blue group-hover:text-white transition-colors" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* Final CTA Banner - Join the Community */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative h-[500px] overflow-hidden"
        >
          <img
            src={Event3}
            // src="https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1200&q=80"
            alt="Join the adventure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/95 via-slate-900/80 to-mamba-navy/70" />
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="text-center max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full mb-6">
                  <Sparkles className="w-4 h-4 text-mamba-golden" />
                  <span className="text-mamba-golden font-bold font-montserrat text-sm uppercase tracking-wider">
                    Join Our Community
                  </span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-white mb-6 leading-tight">
                  Ready to Write
                  <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                    Your Adventure Story?
                  </span>
                </h2>
                <p className="text-xl font-dancing italic text-white/90 mb-10 leading-relaxed max-w-2xl mx-auto">
                  Join thousands of adventurers who've discovered their limits
                  and exceeded them. Your journey begins here.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  {adventure?.booking_link ? (
                    <a
                      href={adventure.booking_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-black font-montserrat text-lg rounded-full shadow-2xl hover:shadow-mamba-golden/50 transition-all uppercase"
                      >
                        <span>Book Your Adventure</span>
                        <ArrowRight className="w-6 h-6" />
                      </motion.button>
                    </a>
                  ) : null}
                  <a
                    href="https://www.instagram.com/mamba_hikers/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-3 px-10 py-5 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white font-bold text-lg rounded-full hover:bg-white/20 transition-all"
                    >
                      <Instagram className="w-6 h-6" />
                      <span>Follow Us</span>
                    </motion.button>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Sticky Pricing Banner */}
      <AnimatePresence>
        {showPricingBanner && adventure?.booking_link && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, type: "spring", damping: 25 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-2xl"
          >
            <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
              <div className="flex items-center justify-between gap-4">
                {/* Left: Adventure Info */}
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="hidden sm:flex w-12 h-12 rounded-xl bg-gradient-to-br from-mamba-navy to-mamba-sky-blue items-center justify-center">
                    <Mountain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 font-medium">
                      {adventure?.destination_data?.name}
                    </p>
                    <p className="text-xl sm:text-2xl font-black font-montserrat bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                      Book Your Adventure
                    </p>
                  </div>
                </div>

                {/* Right: CTA + Close */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <a
                    href={adventure.booking_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold font-montserrat rounded-full shadow-lg hover:shadow-xl transition-all text-sm sm:text-base"
                    >
                      Book Now
                    </motion.button>
                  </a>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPricingBanner(false)}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* What's Included Popup Modal */}
      <AnimatePresence>
        {showPricingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            onClick={() => setShowPricingModal(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal Content - Centered */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <ListChecks className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold font-montserrat text-white">
                    What's Included
                  </h3>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowPricingModal(false)}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </motion.button>
              </div>

              {/* Body */}
              <div className="p-8 md:p-10 overflow-y-auto max-h-[calc(90vh-88px)]">
                <div className="prose prose-lg max-w-none text-slate-700 prose-headings:text-mamba-navy prose-headings:font-montserrat prose-li:marker:text-mamba-sky-blue prose-p:leading-relaxed">
                  <HtmlContent htmlData={adventure?.pricing_includes} />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Modal */}
      <AnimatePresence>
        {showGalleryModal && destinationImages?.results && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setShowGalleryModal(false)}
          >
            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowGalleryModal(false)}
              className="absolute top-4 right-4 z-50 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </motion.button>

            {/* Image Counter */}
            <div className="absolute top-4 left-4 z-50 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full">
              <span className="text-white font-semibold">
                {selectedImageIndex + 1} / {destinationImages.results.length}
              </span>
            </div>

            {/* Previous Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex((prev) =>
                  prev === 0 ? destinationImages.results.length - 1 : prev - 1
                );
              }}
              className="absolute left-4 z-50 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </motion.button>

            {/* Main Image */}
            <motion.div
              key={selectedImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={destinationImages.results[selectedImageIndex]?.image_url || NoImage}
                alt={`Gallery ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>

            {/* Next Button */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex((prev) =>
                  prev === destinationImages.results.length - 1 ? 0 : prev + 1
                );
              }}
              className="absolute right-4 z-50 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <ArrowRight className="w-6 h-6 text-white" />
            </motion.button>

            {/* Thumbnail Strip */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
              <div className="flex gap-2 p-2 bg-white/10 backdrop-blur-md rounded-xl max-w-[90vw] overflow-x-auto">
                {destinationImages.results.map((image, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(index);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index
                        ? "border-mamba-golden shadow-lg"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={image.image_url || NoImage}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ClientLayout>
  );
};


export default EventDetailRedesign;
